import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  MapPin, 
  Phone, 
  Globe, 
  Star,
  Loader2,
  ChevronLeft,
  Calendar,
  MessageSquare
} from 'lucide-react';

const CaddieHat = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16s0-4 8-4 8 4 8 4" />
    <path d="M2 16h20" />
    <ellipse cx="12" cy="12" rx="6" ry="4" />
    <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Failed to fetch course:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-bone pt-20">
        <div className="container-padding py-16 text-center">
          <MapPin className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display text-2xl text-deep-green mb-2">Course Not Found</h1>
          <Link to="/explore">
            <Button className="mt-4">Back to Explore</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-24">
      {/* Hero Image */}
      {course.images?.[0] && (
        <div className="h-64 md:h-96 relative">
          <img 
            src={course.images[0]} 
            alt={course.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-deep-green/80 to-transparent" />
        </div>
      )}

      <div className="container-padding py-8">
        <Link to="/explore" className="inline-flex items-center text-muted-foreground hover:text-deep-green mb-4">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Directory
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {course.minority_owned && (
                  <Badge className="bg-gold text-deep-green">
                    <Star className="w-3 h-3 mr-1" />
                    Minority Owned
                  </Badge>
                )}
                {course.ownership_type?.map((type, i) => (
                  <Badge key={i} variant="outline">{type}</Badge>
                ))}
                <Badge variant="outline" className="capitalize">{course.verification_status}</Badge>
              </div>
              <h1 className="font-display font-bold text-3xl md:text-4xl text-deep-green mb-3" data-testid="course-name">
                {course.name}
              </h1>
              <p className="text-muted-foreground flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5" />
                {course.city}, {course.state}
              </p>
            </div>

            {course.amenities?.length > 0 && (
              <Card className="card-premium">
                <CardContent className="p-6">
                  <h2 className="font-display font-semibold text-xl text-deep-green mb-4">Amenities</h2>
                  <div className="flex flex-wrap gap-2">
                    {course.amenities.map((amenity, i) => (
                      <Badge key={i} variant="outline">{amenity}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {course.pricing_notes && (
              <Card className="card-premium">
                <CardContent className="p-6">
                  <h2 className="font-display font-semibold text-xl text-deep-green mb-4">Pricing</h2>
                  <p className="text-muted-foreground">{course.pricing_notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Book Tee Time Card */}
            <Card className="card-premium border-gold/30">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-display font-semibold text-xl text-deep-green flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gold" />
                  Book a Tee Time
                </h2>
                <p className="text-muted-foreground text-sm">
                  Ready to play? Book directly through the course website or let our AI Caddie help you.
                </p>
                {course.website && (
                  <a 
                    href={course.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full btn-gold" data-testid="book-tee-time-external">
                      <Globe className="w-4 h-4 mr-2" />
                      Book on Course Website
                    </Button>
                  </a>
                )}
                <Link to={`/caddie?course=${encodeURIComponent(course.name || '')}`} className="block">
                  <Button 
                    variant="outline" 
                    className="w-full border-fairway text-fairway hover:bg-fairway hover:text-white"
                    data-testid="ask-caddie-tee-time"
                  >
                    <CaddieHat className="w-4 h-4 mr-2" />
                    Ask Caddie for Help
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card className="card-premium">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-display font-semibold text-xl text-deep-green">Contact</h2>
                {course.address && (
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <MapPin className="w-5 h-5 shrink-0 mt-0.5" />
                    <span>{course.address}</span>
                  </div>
                )}
                {course.phone && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Phone className="w-5 h-5" />
                    <a href={`tel:${course.phone}`} className="hover:text-gold">{course.phone}</a>
                  </div>
                )}
                {course.website && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Globe className="w-5 h-5" />
                    <a 
                      href={course.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-gold truncate"
                    >
                      Visit Website
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;
